import styled from 'styled-components';
import { CheckboxLabelProps } from './JRCFormCheckbox.types';
import * as React from 'react';
import { JRCToggle } from '../../JRCInputCheckbox/components/JRCToggle';
import { JRCCheckbox } from '../../JRCInputCheckbox/components/JRCCheckbox';

export const DirectionContainer = styled.div<{ direction?: string; variant?: string }>`
    display: flex;
    flex-direction: ${(props) => (props.direction === 'column' ? 'column' : 'row')};
    flex-wrap: wrap;
    align-items: ${(props) =>
        props.direction === 'column' && props.variant === 'outlined' ? 'stretch' : 'flex-start'};
    gap: 8px;
`;

export const FocusBackground = styled.div<{ variant?: 'default' | 'outlined' }>`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: 0;
    border-radius: ${(props) => (props.variant === 'outlined' ? '8px' : '0')};
    z-index: -1;
`;

export const CheckboxLabel = styled.label<CheckboxLabelProps>`
    position: relative;
    z-index: 0;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    ${(props) => props.labelPosition === 'after' && 'flex-direction: row-reverse'};
    ${(props) => props.labelPosition === 'after' && 'justify-content: space-between'};
    ${(props) => {
        if (props.direction === 'two-columns') return 'width: calc(50% - 4px)';
        else if (props.direction === 'column') return 'width: 100%';
        return;
    }};
    min-height: ${(props) => {
        if (props.variant === 'outlined') {
            if (props.direction === 'two-columns') return '64px';
            else if (props.direction === 'column') return '92px';
        }
        return '28px';
    }};
    padding: ${(props) => {
        if (props.variant === 'outlined') {
            if (props.direction === 'row')
                return props.labelPosition === 'after' ? '4px 4px 4px 8px' : '4px 8px 4px 4px';
            return props.labelPosition === 'after' ? '8px 8px 8px 16px' : '8px 16px 8px 8px';
        }
        return;
    }};
    border-radius: ${(props) => (props.variant === 'outlined' ? '8px' : '0')};
    ${(props) => {
        if (props.variant === 'outlined' && props.checked) return `border: 1px solid ${props.theme.color.navy}`;
        else if (props.variant === 'outlined') return `border: 1px solid ${props.theme.color.grey2}`;
        return;
    }};
    color: ${(props) => {
        if (props.disabled) return props.theme.color.grey3;
        else return props.theme.color.grey5;
    }};
    ${(props) => !props.disabled && 'cursor: pointer'};
    transition: border 0.2s;

    &:hover {
        ${(props) => props.backgroundOnHover && !props.disabled && `background-color: ${props.theme.color.grey0}`};
        ${(props) => props.variant === 'outlined' && !props.disabled && `border: 1px solid ${props.theme.color.navy}`};
    }

    &:not(:last-child) {
        ${(props) => props.direction === 'row' && 'margin-right: 8px'};
    }

    &:nth-child(odd) {
        ${(props) => props.direction === 'two-columns' && 'margin-right: 8px'};
    }

    & input:focus-visible + div {
        background-color: ${(props) => props.theme.color.grey1};
        border-radius: ${(props) => (props.variant === 'outlined' ? '8px' : '0')};
    }

    &:hover .checkbox-like,
    &:hover .radio-like {
        background-color: ${(props) => props.theme.color.grey0};
    }

    & > ${FocusBackground} {
        ${(props) => props.checked && props.backgroundOnSelect && `background-color: ${props.theme.color.grey0}`};
    }
`;

export type CheckboxOrToggleProps = Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> & {
    checkboxMode?: string;
};
const CheckboxOrToggleElement = function (props: CheckboxOrToggleProps, ref: React.ForwardedRef<HTMLInputElement>) {
    const { checkboxMode, ...inputProps } = props;
    return checkboxMode === 'toggle' ? (
        <JRCToggle {...inputProps} ref={ref} />
    ) : (
        <JRCCheckbox {...inputProps} ref={ref} />
    );
};
export const CheckboxOrToggle = React.forwardRef(CheckboxOrToggleElement);
